import { useCallback, useRef, useState } from 'react'
import type { PointerEvent as ReactPointerEvent, WheelEvent as ReactWheelEvent } from 'react'
import type { MutableRefObject } from 'react'
import { clamp, distance, midpoint, type PointerPoint } from '../imageZoomUtils'

type Params = {
  minScale: number
  maxScale: number
  containerSizeRef: MutableRefObject<{ width: number; height: number }>
}

export const useImageZoomGestures = ({ minScale, maxScale, containerSizeRef }: Params) => {
  const pointersRef = useRef<Map<number, PointerPoint>>(new Map())
  const dragStartPointerRef = useRef<PointerPoint | null>(null)
  const dragStartOffsetRef = useRef<PointerPoint>({ x: 0, y: 0 })
  const pinchStartDistanceRef = useRef(0)
  const pinchStartScaleRef = useRef(1)
  const pinchStartOffsetRef = useRef<PointerPoint>({ x: 0, y: 0 })
  const pinchStartCenterRef = useRef<PointerPoint | null>(null)

  const [scale, setScale] = useState(1)
  const [offset, setOffset] = useState<PointerPoint>({ x: 0, y: 0 })
  const [isDragging, setIsDragging] = useState(false)

  const clampOffset = useCallback(
    (nextScale: number, nextOffset: PointerPoint): PointerPoint => {
      const { width, height } = containerSizeRef.current
      if (nextScale <= 1 || width <= 0 || height <= 0) return { x: 0, y: 0 }
      const maxX = ((nextScale - 1) * width) / 2
      const maxY = ((nextScale - 1) * height) / 2
      return {
        x: clamp(nextOffset.x, -maxX, maxX),
        y: clamp(nextOffset.y, -maxY, maxY),
      }
    },
    [containerSizeRef],
  )

  const applyScale = useCallback(
    (nextScaleRaw: number, nextOffset?: PointerPoint) => {
      const nextScale = clamp(nextScaleRaw, minScale, maxScale)
      setScale(nextScale)
      if (nextScale === 1) {
        setOffset({ x: 0, y: 0 })
        return
      }
      setOffset((current) => clampOffset(nextScale, nextOffset ?? current))
    },
    [clampOffset, maxScale, minScale],
  )

  const handleWheel = useCallback(
    (event: ReactWheelEvent<HTMLDivElement>) => {
      event.preventDefault()
      const delta = event.deltaY > 0 ? -0.2 : 0.2
      applyScale(scale + delta)
    },
    [applyScale, scale],
  )

  const handlePointerDown = useCallback(
    (event: ReactPointerEvent<HTMLDivElement>) => {
      event.currentTarget.setPointerCapture(event.pointerId)
      pointersRef.current.set(event.pointerId, { x: event.clientX, y: event.clientY })
      const pointers = Array.from(pointersRef.current.values())

      if (pointers.length === 1 && scale > 1) {
        dragStartPointerRef.current = { x: event.clientX, y: event.clientY }
        dragStartOffsetRef.current = offset
        setIsDragging(true)
      }

      if (pointers.length === 2) {
        const [left, right] = pointers
        pinchStartDistanceRef.current = Math.max(distance(left, right), 1)
        pinchStartScaleRef.current = scale
        pinchStartOffsetRef.current = offset
        pinchStartCenterRef.current = midpoint(left, right)
        dragStartPointerRef.current = null
        setIsDragging(false)
      }
    },
    [offset, scale],
  )

  const handlePointerMove = useCallback(
    (event: ReactPointerEvent<HTMLDivElement>) => {
      if (!pointersRef.current.has(event.pointerId)) return
      pointersRef.current.set(event.pointerId, { x: event.clientX, y: event.clientY })
      const pointers = Array.from(pointersRef.current.values())

      if (pointers.length === 2) {
        const [left, right] = pointers
        const nextDistance = Math.max(distance(left, right), 1)
        const ratio = nextDistance / pinchStartDistanceRef.current
        const nextScale = clamp(pinchStartScaleRef.current * ratio, minScale, maxScale)
        const center = midpoint(left, right)
        const startCenter = pinchStartCenterRef.current ?? center
        const nextOffset = {
          x: pinchStartOffsetRef.current.x + (center.x - startCenter.x),
          y: pinchStartOffsetRef.current.y + (center.y - startCenter.y),
        }
        setScale(nextScale)
        if (nextScale === 1) {
          setOffset({ x: 0, y: 0 })
        } else {
          setOffset(clampOffset(nextScale, nextOffset))
        }
        return
      }

      if (pointers.length === 1 && dragStartPointerRef.current && scale > 1) {
        const deltaX = event.clientX - dragStartPointerRef.current.x
        const deltaY = event.clientY - dragStartPointerRef.current.y
        const nextOffset = {
          x: dragStartOffsetRef.current.x + deltaX,
          y: dragStartOffsetRef.current.y + deltaY,
        }
        setOffset(clampOffset(scale, nextOffset))
      }
    },
    [clampOffset, maxScale, minScale, scale],
  )

  const handlePointerUp = useCallback((event: ReactPointerEvent<HTMLDivElement>) => {
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId)
    }
    pointersRef.current.delete(event.pointerId)
    if (pointersRef.current.size < 2) {
      pinchStartCenterRef.current = null
      pinchStartDistanceRef.current = 0
    }
    if (pointersRef.current.size === 0) {
      dragStartPointerRef.current = null
      setIsDragging(false)
    }
  }, [])

  const handlePointerCancel = useCallback((event: ReactPointerEvent<HTMLDivElement>) => {
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId)
    }
    pointersRef.current.delete(event.pointerId)
    if (pointersRef.current.size === 0) {
      dragStartPointerRef.current = null
      setIsDragging(false)
    }
  }, [])

  return {
    scale,
    offset,
    isDragging,
    applyScale,
    handleWheel,
    handlePointerDown,
    handlePointerMove,
    handlePointerUp,
    handlePointerCancel,
  }
}
